import { useDataProvider, useNotify, type Identifier } from "ra-core";
import { useCallback } from "react";

import type { PipelineCard, PipelineStage } from "../../types";
import { getDropPosition, getPositionAt, sortByPosition } from "../positions";
import { useRefreshBoard } from "../useRefreshBoard";

/** Stage operations of a pipeline: rename, reorder, add, delete. */
export const useStageActions = (
  pipelineId: Identifier | undefined,
  stages: PipelineStage[],
) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refreshBoard = useRefreshBoard();

  const run = useCallback(
    async (action: () => Promise<unknown>, message: string) => {
      try {
        await action();
        await refreshBoard();
        notify(message);
      } catch (error: unknown) {
        notify(
          error instanceof Error ? error.message : "ra.notification.http_error",
          { type: "error" },
        );
        throw error;
      }
    },
    [notify, refreshBoard],
  );

  const rename = (stage: PipelineStage, name: string) =>
    run(
      () =>
        dataProvider.update("pipeline_stages", {
          id: stage.id,
          data: { name },
          previousData: stage,
        }),
      "crm.operations.stages.updated",
    );

  const move = (stage: PipelineStage, direction: -1 | 1) => {
    const sorted = sortByPosition(stages);
    const index = sorted.findIndex((candidate) => candidate.id === stage.id);
    return run(
      () =>
        dataProvider.update("pipeline_stages", {
          id: stage.id,
          data: {
            position: getDropPosition(sorted, stage.id, index + direction),
          },
          previousData: stage,
        }),
      "crm.operations.stages.updated",
    );
  };

  /** Adds a stage after `afterStage`, or at the end when it is null. */
  const add = (name: string, afterStage: PipelineStage | null) => {
    const sorted = sortByPosition(stages);
    const index = afterStage
      ? sorted.findIndex((candidate) => candidate.id === afterStage.id) + 1
      : sorted.length;
    return run(
      () =>
        dataProvider.create("pipeline_stages", {
          data: {
            pipeline_id: pipelineId,
            name,
            position: getPositionAt(sorted, index),
          },
        }),
      "crm.operations.stages.created",
    );
  };

  /** Moves the stage's cards (if any) to the bottom of `targetStageId`, then deletes it. */
  const remove = (stage: PipelineStage, targetStageId: Identifier | null) =>
    run(async () => {
      if (targetStageId != null) {
        const [{ data: cards }, { data: targetCards }] = await Promise.all(
          [stage.id, targetStageId].map((stageId) =>
            dataProvider.getList<PipelineCard>("pipeline_cards", {
              pagination: { page: 1, perPage: 5000 },
              sort: { field: "position", order: "ASC" },
              filter: { pipeline_id: pipelineId, stage_id: stageId },
            }),
          ),
        );
        const start = getPositionAt(targetCards, targetCards.length);
        await Promise.all(
          cards.map((card, offset) =>
            dataProvider.update("pipeline_cards", {
              id: card.id,
              data: { stage_id: targetStageId, position: start + offset },
              previousData: card,
            }),
          ),
        );
      }
      await dataProvider.delete("pipeline_stages", {
        id: stage.id,
        previousData: stage,
      });
    }, "crm.operations.stages.deleted");

  return { rename, move, add, remove };
};
